/**
 * The mobile navigation ROOT — the Settings.app model, in look as well as structure.
 *
 * WHY THIS EXISTS: a tab bar is the classic iOS pattern for an app with FEW destinations. This app
 * has 12 sections / 26 destinations in workspace context, which is the other classic shape — the
 * one Settings, Mail and Files use: a drill-down stack. Navigation stops being an overlay you pull
 * over the page (a Material/desktop idiom, and the single strongest "this is a ported web app"
 * signal) and becomes screens you push through:
 *
 *     /ws/menu            sections, each a row with a chevron
 *       └─ /ws/menu/:id   that section's destinations
 *            └─ the page itself
 *
 * Every level is a real route, so back, the edge swipe and the push animation all describe the same
 * hierarchy rather than three different ideas of it. The tree is `nav.tsx` — the same source the
 * desktop rail and the command palette read, so a destination cannot exist in one and not the other.
 *
 * Desktop never renders this: it has the rail and the section column, where 26 destinations fit.
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingsGroup, SettingsRow, SettingsPage, SettingsTitle } from '@teivor/shared-ui';

import { navigateWithPush } from '../lib/nav-history';
import { useMeQuery } from '../features/settings/queries';
import {
  useIsInsideWorkspace,
  useLeaveWorkspace,
  useSelectedWorkspaceId,
} from '../lib/workspace-context';
import { useWorkspacesQuery } from '../features/admin/queries';
import { visibleSystemNav, visibleWorkspaceNav, type NavNode } from '../lib/nav';

export function MenuPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId?: string }>();
  const me = useMeQuery();
  const role = me.data?.role;
  const isInsideWorkspace = useIsInsideWorkspace();

  const nodes: NavNode[] = React.useMemo(
    () => (isInsideWorkspace ? visibleWorkspaceNav(role) : visibleSystemNav(role)),
    [isInsideWorkspace, role],
  );

  const displayName =
    [me.data?.firstName, me.data?.lastName].filter(Boolean).join(' ') || me.data?.email || '';

  const base = isInsideWorkspace ? '/ws/menu' : '/menu';
  const section = sectionId ? nodes.find((n) => n.id === sectionId) : undefined;

  const selectedWorkspaceId = useSelectedWorkspaceId();
  const leaveWorkspace = useLeaveWorkspace();
  const { data: workspacesData } = useWorkspacesQuery();
  const currentWorkspace = React.useMemo(() => {
    if (!selectedWorkspaceId || !workspacesData?.items) return null;
    return workspacesData.items.find((w) => w.id === selectedWorkspaceId) ?? null;
  }, [selectedWorkspaceId, workspacesData]);

  const onLeave = () => {
    leaveWorkspace();
    navigate('/shell/workspaces');
  };

  // A section that does not exist for this role — a stale deep link, or a bookmark kept after a
  // permission change. Fall through to the root rather than an empty screen with a back to nowhere.
  const showSection = !!section;

  if (showSection && section) {
    return (
      <SettingsPage>
        <SettingsTitle>{t(section.labelKey, section.id)}</SettingsTitle>
        <SettingsGroup>
          {section.items.map((item, i) => (
            <SettingsRow
              key={item.to}
              label={t(item.labelKey, item.to)}
              onPress={() => navigateWithPush(navigate, item.to, 'forward')}
              last={i === section.items.length - 1}
            />
          ))}
        </SettingsGroup>
      </SettingsPage>
    );
  }

  return (
    <SettingsPage>
      <SettingsTitle>{t('nav.menu', 'Menu')}</SettingsTitle>

      {/* The account, as the FIRST row — where Settings puts the Apple Account. The avatar was
          removed from the mobile topbar (permanent chrome that never changes), so this is now the
          way to reach profile and preferences on a phone. */}
      <SettingsGroup>
        <SettingsRow
          label={displayName || t('nav.account', 'Účet')}
          detail={me.data?.email}
          onPress={() => navigateWithPush(navigate, '/settings/profile', 'forward')}
        />
        <SettingsRow
          label={t('nav.preferences', 'Předvolby')}
          onPress={() => navigateWithPush(navigate, '/settings/preferences', 'forward')}
          last
        />
      </SettingsGroup>

      {/* Which workspace you are in, and the way out. Lives here because the menu IS the mobile
          navigation root now — the drawer that used to hold it is no longer reachable, and both the
          name and the leave control were desktop-only before that. */}
      {isInsideWorkspace && currentWorkspace ? (
        <SettingsGroup header={t('nav.currentWorkspace', 'Pracovní prostor')}>
          <SettingsRow label={currentWorkspace.name} />
          <SettingsRow
            label={t('nav.leaveWorkspace', 'Opustit prostor')}
            onPress={onLeave}
            chevron={false}
            destructive
            last
          />
        </SettingsGroup>
      ) : null}

      <SettingsGroup>
        {nodes.map((n, i) => {
          const Icon = n.glyph.line;
          // A section holding ONE destination is not a section — pushing a list of one is a wasted
          // screen and a second tap for nothing. Go straight to the page, exactly as Settings sends
          // you directly rather than through a single-row list.
          const only = n.items.length === 1 ? n.items[0] : null;
          return (
            <SettingsRow
              key={n.id}
              icon={<Icon size={22} color="currentColor" aria-hidden />}
              label={t(n.labelKey, n.id)}
              onPress={() =>
                navigateWithPush(navigate, only ? only.to : `${base}/${n.id}`, 'forward')
              }
              last={i === nodes.length - 1}
            />
          );
        })}
      </SettingsGroup>
    </SettingsPage>
  );
}
